import React, { useEffect, useMemo, useState } from 'react';

// Hooks
import { useDispatch } from 'react-redux';
import { failure } from '~/redux/actions/snackbar-actions';

// Utils
import { createEnvelopeProof, createViewProofForOrder } from '~/utils/template-builder';
import { CONTACTS_OPTION_TYPE, HAS_ENVELOPE, PROOF_TABS } from '~/utils/constants';
// Temporarily disabled: proof-download production restriction is commented below.
// import { numberWithComma, isProduction } from '~/utils/helperFunctions';
import { numberWithComma } from '~/utils/helperFunctions';
import { MESSAGES } from '~/utils/message';

// Components
import OneOfThumbnail from '~/components/Orders/CreateOrder/MailerDetails/OneOfThumbnail';

// MUI Components
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  CircularProgress,
} from '@mui/material';

// icons
import DoneIcon from '@mui/icons-material/Done';

// Styles
import './styles.scss';
import './orderedProductsOverrides.scss';
import OrderedProducts from './OrderedProducts';
import { CheckedCIcon, MailerProofReviewCIcon, UncheckedCIcon } from '~/assets/images';

const ReviewOrder = (props) => {
  const {
    selectedTemplate,
    forntImage,
    envelopeImage,
    template,
    deliverable,
    checkedIds,
    setCheckedIds,
    selectedReturnedAddress,
    contactType,
    selectedContact,
    name,
    errors,
    updateErrors,
    isTemplateAvailable,
    setOrderProofDownloaded,
    setEnvelopeProofDownloaded,
    products,
    selectedEnvelope,
    selectedPostage,
    selectionType,
    selectedCampaign,
    isFlipped,
    setFlipped,
    handleTabChange,
    tabValue,
    setTabValue,
    downloadingRows,
    setDownloadingRows,
    activeItemId,
    expectedDateData,
    selectedDate,
    onValidationChange,
  } = props;
  const [downloadingProof, setDownloadingProof] = useState(false);
  const [downloadingMailerProof, setDownloadingMailerProof] = useState(false);
  const [downloadingEnvelopeProof, setDownloadingEnvelopeProof] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [isBackSide, setIsBackSide] = useState(false);
  const [templatePreview, setTemplatePreview] = useState({
    templateUrl: null,
    backTemplateUrl: null,
  });
  // Track downloads to force validation recalculation
  const [downloadUpdateTrigger, setDownloadUpdateTrigger] = useState(0);
  const previewTab = tabValue?.type === 'mail_proof' ? 'mailer' : 'envelope';

  const dispatch = useDispatch();

  // More robust envelope detection for rendering tabs/buttons
  const hasEnvelope = useMemo(() => {
    const productHasEnvelope = HAS_ENVELOPE.includes(+(selectedTemplate?.productId || 0));
    const hasThumb =
      !!envelopeImage ||
      !!selectedTemplate?.envelopeThumbnailUrl ||
      !!selectedTemplate?.envelopeType;
    return productHasEnvelope || hasThumb;
  }, [selectedTemplate, envelopeImage]);

  const templateHasEnvelopeProof = (template) => {
    if (!template) return false;
    return (
      HAS_ENVELOPE.includes(+(template?.productId || 0)) ||
      !!template?.envelopeThumbnailUrl ||
      !!template?.envelopeType ||
      !!template?.selectedEnvelope ||
      !!template?.isEnvelopeDownloaded
    );
  };

  // Resolve mailer thumbnail robustly (with fallbacks)
  const mailerImageUrl = useMemo(() => {
    return (
      templatePreview?.templateUrl ||
      selectedTemplate?.frontThumbnailUrl ||
      template?.frontThumbnailUrl ||
      forntImage ||
      selectedTemplate?.object?.thumbnailUrl ||
      null
    );
  }, [templatePreview?.templateUrl, selectedTemplate, template, forntImage]);

  const activePreviewImageUrl = useMemo(() => {
    if (previewTab === 'envelope') return envelopeImage || null;
    if (isBackSide && templatePreview.backTemplateUrl) return templatePreview.backTemplateUrl;
    return mailerImageUrl;
  }, [previewTab, envelopeImage, isBackSide, templatePreview.backTemplateUrl, mailerImageUrl]);

  // Temporarily disabled: production proof downloads should not block order creation.
  // const getProofDownloadCounts = () => {
  //   const mailerDownloaded = getDownloadedProofsCount();
  //   const mailerTotal = getTotalMailerProofsCount();
  //
  //   const envelopeTotal = getTotalEnvelopeProofsCount();
  //   const envelopeDownloaded = getDownloadedEnvelopeProofsCount();
  //
  //   const downloaded = mailerDownloaded + envelopeDownloaded;
  //   const total = mailerTotal + envelopeTotal;
  //
  //   return { downloaded, total };
  // };

  const handleCheckboxChange = (contentId) => {
    // Temporarily disabled: production proof downloads should not block order creation.
    // if (contentId === 1 && !checkedIds.includes(contentId) && isProduction()) {
    //   const { downloaded, total } = getProofDownloadCounts();
    //   if (downloaded !== total) {
    //     dispatch(failure('Please download all proofs first to continue.'));
    //     return; // Prevent checkbox from being checked
    //   }
    // }

    // Calculate what the new checkedIds will be
    const newCheckedIds = checkedIds.includes(contentId)
      ? checkedIds.filter((id) => id !== contentId)
      : [...checkedIds, contentId];

    // Proof review checkbox is still required; only actual proof download verification is disabled.
    const requiredCheckboxIds = [1, 3, 4, 5];
    // const requiredCheckboxIds = isProduction() ? [1, 3, 4, 5] : [3, 4, 5];

    // Check if all required checkboxes will be checked after this change
    const allRequiredChecked = requiredCheckboxIds.every((id) => newCheckedIds.includes(id));

    // Only clear the error if all required checkboxes are checked
    if (allRequiredChecked) {
      updateErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }

    // Update the checkedIds
    setCheckedIds(newCheckedIds);
  };

  // Calculate downloaded proofs for campaigns
  const getDownloadedProofsCount = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      return selectedCampaign.templates.filter((template) => template.isDownloaded).length;
    }
    // Single mailer: count 1 if downloaded, else 0
    if (selectionType?.id === 1) {
      return selectedTemplate?.isDownloaded ? 1 : 0;
    }
    return 0;
  };

  const getTotalMailerProofsCount = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      return selectedCampaign.templates.length;
    }
    return 1;
  };

  const getTotalEnvelopeProofsCount = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      return selectedCampaign.templates.filter((template) => templateHasEnvelopeProof(template))
        .length;
    }
    return hasEnvelope ? 1 : 0;
  };

  const getDownloadedEnvelopeProofsCount = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      return selectedCampaign.templates.filter(
        (template) => templateHasEnvelopeProof(template) && template.isEnvelopeDownloaded
      ).length;
    }
    return hasEnvelope && selectedTemplate?.isEnvelopeDownloaded ? 1 : 0;
  };

  // Resolve a robust, non-empty label to use for downloaded filenames
  const getDownloadLabel = (currentProofIndex = null) => {
    if (selectionType?.id === 2) {
      const t = selectedCampaign?.templates?.[currentProofIndex] || {};
      // Prefer existing label if present
      if (t.label) return t.label;
      const id = t.id;
      const titleCandidate =
        t.title || t.name || t.templateTitle || t?.template?.title || t?.object?.title || '';
      if (id && titleCandidate) return `ID:${id} ${titleCandidate}`;
      if (titleCandidate) return titleCandidate;
      if (id) return `ID:${id}`;
      return 'Template';
    }

    // Single mailer
    const single = template || selectedTemplate || {};
    // Prefer any existing label
    if (single.label) return single.label;
    const id = single.id;
    const titleCandidate =
      single.title ||
      single.name ||
      single.templateTitle ||
      single?.template?.title ||
      single?.object?.title ||
      '';
    if (id && titleCandidate) return `ID:${id} ${titleCandidate}`;
    if (titleCandidate) return titleCandidate;
    if (id) return `ID:${id}`;
    return 'Template';
  };

  // Generate dynamic proof download label
  const getProofDownloadLabel = () => {
    // Combined mailer + envelope counts and dynamic progress
    const mailerDownloaded = getDownloadedProofsCount();
    const mailerTotal = getTotalMailerProofsCount();

    const envelopeTotal = getTotalEnvelopeProofsCount();
    const envelopeDownloaded = getDownloadedEnvelopeProofsCount();

    const downloaded = mailerDownloaded + envelopeDownloaded;
    const total = mailerTotal + envelopeTotal;

    if (mailerDownloaded === mailerTotal) setOrderProofDownloaded(true);
    if (envelopeTotal > 0 && envelopeDownloaded === envelopeTotal) setEnvelopeProofDownloaded(true);

    return (
      <>
        I have downloaded and reviewed {total > 1 && `all`} required proof{total > 1 && `s`}. -{' '}
        <span style={{ color: '#575757', fontWeight: '700' }}>
          {downloaded} of {total}
        </span>{' '}
        downloaded
      </>
    );
  };

  // Check if all proofs are downloaded for campaigns
  const areAllProofsDownloaded = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      return selectedCampaign.templates.every((template) => template.isDownloaded);
    }
    return selectedTemplate?.isDownloaded || false;
  };

  // Check if all envelope proofs are downloaded for campaigns
  const areAllEnvelopeProofsDownloaded = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      const templatesWithEnvelope = selectedCampaign.templates.filter((template) =>
        templateHasEnvelopeProof(template)
      );
      if (!templatesWithEnvelope.length) return true;
      return templatesWithEnvelope.every((template) => template.isEnvelopeDownloaded);
    }
    if (!hasEnvelope) return true;
    return !!selectedTemplate?.isEnvelopeDownloaded;
  };

  // Combined "all required proofs" status for auto-checking the single checkbox
  const areAllRequiredProofsDownloaded = () => {
    if (selectionType?.id === 2 && selectedCampaign?.templates) {
      const hasAnyEnvelope = selectedCampaign.templates.some((t) => templateHasEnvelopeProof(t));
      return hasAnyEnvelope
        ? areAllProofsDownloaded() && areAllEnvelopeProofsDownloaded()
        : areAllProofsDownloaded();
    }
    // Single mailer
    if (hasEnvelope) {
      return !!(selectedTemplate?.isDownloaded && selectedTemplate?.isEnvelopeDownloaded);
    }
    return !!selectedTemplate?.isDownloaded;
  };

  const getDeliverableLabel = () => {
    return (
      <>
        My order is set to mail to{' '}
        <span style={{ color: '#575757', fontWeight: '700' }}>{numberWithComma(deliverable)}</span>{' '}
        {deliverable <= 1
          ? MESSAGES.ORDERS.CREATE.REVIEW.DELIVERABLE_TITLE
          : MESSAGES.ORDERS.CREATE.REVIEW.DELIVERABLES_TITLE}
      </>
    );
  };

  const checkList = [
    {
      id: 1,
      label: getProofDownloadLabel(),
      isCompleted: areAllProofsDownloaded(),
    },
    {
      id: 3,
      label:
        'I confirm that my proof displays correctly, all spelling is accurate, and variable fields are properly merged with my contact data.',
    },
    {
      id: 4,
      label: getDeliverableLabel(),
    },
    {
      id: 5,
      label: 'I confirm that the mail dates and batching settings I selected are correct.',
    },
  ].filter((item) => item !== false);

  // Check if all required checkboxes are checked. Actual proof downloads remain optional for now.
  const areAllRequiredCheckboxesChecked = useMemo(() => {
    // Proof review checkbox is still required; only actual proof download verification is disabled.
    const requiredCheckboxIds = [1, 3, 4, 5];
    // const requiredCheckboxIds = isProduction() ? [1, 3, 4, 5] : [3, 4, 5];
    return requiredCheckboxIds.every((id) => checkedIds.includes(id));
  }, [checkedIds]);

  // Temporarily disabled: production proof downloads should not block order creation.
  // const areAllProofsDownloadedCompletely = useMemo(() => {
  //   const mailerDownloaded = getDownloadedProofsCount();
  //   const mailerTotal = getTotalMailerProofsCount();
  //   const envelopeTotal = getTotalEnvelopeProofsCount();
  //   const envelopeDownloaded = getDownloadedEnvelopeProofsCount();
  //   const downloaded = mailerDownloaded + envelopeDownloaded;
  //   const total = mailerTotal + envelopeTotal;
  //
  //   // Return true if downloaded equals total (and total > 0 to avoid false positives)
  //   return total > 0 && downloaded === total;
  // }, [selectedTemplate, selectedCampaign, selectionType, downloadUpdateTrigger, hasEnvelope]);

  // Combined validation: proof download restriction is optional for now.
  const isReviewValid = useMemo(() => {
    // if (!areAllRequiredCheckboxesChecked) return false;
    // if (!isProduction()) return true;
    // return areAllProofsDownloadedCompletely;
    return areAllRequiredCheckboxesChecked;
  }, [areAllRequiredCheckboxesChecked]);

  // Accept index for row, and manage loader state
  const downloadViewProof = async (currentProofIndex = null) => {
    try {
      if (deliverable <= 0) {
        dispatch(failure(MESSAGES.ORDERS.CREATE.PROOF_REQUIREMENT));
        return;
      }

      // Validate required data before proceeding
      if (!selectedReturnedAddress) {
        dispatch(failure('Return address is required'));
        return;
      }

      if (!selectedContact) {
        dispatch(failure('Contact selection is required'));
        return;
      }

      // Validate template data based on selection type
      if (selectionType?.id === 1) {
        if (!selectedTemplate?.id) {
          dispatch(failure('Template is required'));
          return;
        }
      } else if (selectionType?.id === 2) {
        const campaignTemplate = selectedCampaign?.templates?.[currentProofIndex];
        if (!campaignTemplate?.id) {
          dispatch(failure('Template is required'));
          return;
        }
      }

      setDownloadingRows((prev) => [...prev, { index: currentProofIndex, type: 'mailer' }]);
      let key = CONTACTS_OPTION_TYPE[contactType];
      key = key === 'tag' ? 'tagId' : key;
      const labelRaw = getDownloadLabel(currentProofIndex);
      const label = (
        typeof labelRaw === 'string' && labelRaw.trim().length > 0
          ? labelRaw
          : `Order-${selectionType?.id === 1 ? selectedTemplate?.id || 'Proof' : selectedCampaign?.templates?.[currentProofIndex]?.id || 'Proof'}`
      )
        .replace(/[\\/:*?"<>|]+/g, '_')
        .slice(0, 150);
      const templateId =
        selectionType?.id === 1
          ? selectedTemplate?.id
          : +selectedCampaign?.templates?.[currentProofIndex]?.id;

      if (!templateId) {
        dispatch(failure('Template is required'));
        return;
      }

      // Build contactIds as an array of numbers when multiple contacts are selected
      const contactIds =
        key === 'contactIds'
          ? Array.isArray(selectedContact?.id)
            ? selectedContact.id
                .map((val) => (val !== null && val !== undefined ? +val : null))
                .filter((val) => Number.isFinite(val))
            : String(selectedContact?.id || '')
                .split(',')
                .map((val) => (val ? +val : null))
                .filter((val) => Number.isFinite(val))
          : undefined;

      // For reqId: pass-through; for contactIds: send array; for single contactId: pass id
      const contactValue =
        key === 'reqId'
          ? selectedContact
          : key === 'contactIds'
            ? contactIds || []
            : selectedContact?.id;

      let payload = {
        templateId: String(templateId),
        mailedDate: expectedDateData?.mailedDate,
        returnContactId:
          selectedReturnedAddress?.id === '-1' ? undefined : selectedReturnedAddress?.id,
        [key]: contactValue,
      };

      setDownloadingProof(true);
      if (selectionType?.id === 1) setDownloadingMailerProof(true);
      await dispatch(createViewProofForOrder(label, payload));
      if (selectionType?.id === 1) {
        setOrderProofDownloaded(true);
      }

      // Mark proof as downloaded for the current template
      if (selectionType?.id === 2 && selectedCampaign) {
        // Campaigns: multiple templates
        const template = selectedCampaign.templates?.[currentProofIndex];
        if (template) {
          template.isDownloaded = true;
          // Trigger validation recalculation
          setDownloadUpdateTrigger((prev) => prev + 1);
        }

        // Auto-check only when ALL required proofs (mailer + envelope, if any) are downloaded
        if (areAllRequiredProofsDownloaded() && !checkedIds.includes(1)) {
          setCheckedIds((prev) => [...prev, 1]);
        }
      } else if (selectionType?.id === 1 && selectedTemplate) {
        // OneOf: single template
        selectedTemplate.isDownloaded = true;
        // Trigger validation recalculation
        setDownloadUpdateTrigger((prev) => prev + 1);
        // Auto-check only when ALL required proofs are downloaded
        if (areAllRequiredProofsDownloaded() && !checkedIds.includes(1)) {
          setCheckedIds((prev) => [...prev, 1]);
        }
      }
      return true;
    } catch (error) {
      console.log('Error downloading proof:', error);
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      return false;
    } finally {
      setDownloadingProof(false);
      if (selectionType?.id === 1) setDownloadingMailerProof(false);
      setDownloadingRows((prev) =>
        prev.filter((row) => !(row.index === currentProofIndex && row.type === 'mailer'))
      );
    }
  };

  // Accept index for row, and manage loader state
  const downloadEnvelopeProof = async (currentProofIndex = null) => {
    try {
      if (deliverable <= 0) {
        dispatch(failure(MESSAGES.ORDERS.CREATE.PROOF_REQUIREMENT));
        return;
      }

      // Validate required data before proceeding
      if (!selectedReturnedAddress) {
        dispatch(failure('Return address is required'));
        return;
      }

      if (!selectedContact) {
        dispatch(failure('Contact selection is required'));
        return;
      }

      // Validate template data based on selection type
      if (selectionType?.id === 1) {
        if (!template?.id && !selectedTemplate?.id) {
          dispatch(failure('Template is required'));
          return;
        }
      } else if (selectionType?.id === 2) {
        const campaignTemplate = selectedCampaign?.templates?.[currentProofIndex];
        if (!campaignTemplate?.id) {
          dispatch(failure('Template is required'));
          return;
        }
      }

      setDownloadingRows((prev) => [...prev, { index: currentProofIndex, type: 'envelope' }]);
      let key = CONTACTS_OPTION_TYPE[contactType];
      key = key === 'tag' ? 'tagId' : key;
      const labelRaw = getDownloadLabel(currentProofIndex);
      const label = (
        typeof labelRaw === 'string' && labelRaw.trim().length > 0
          ? labelRaw
          : `Order-${selectionType?.id === 1 ? template?.id || selectedTemplate?.id || 'Proof' : selectedCampaign?.templates?.[currentProofIndex]?.id || 'Proof'}`
      )
        .replace(/[\\/:*?"<>|]+/g, '_')
        .slice(0, 150);
      let payload = {};
      if (selectionType?.id === 1) {
        const templateId = template?.id || selectedTemplate?.id;
        if (!templateId) {
          dispatch(failure('Template ID is required'));
          return;
        }
        // Build contactIds as an array of numbers when multiple contacts are selected
        const contactIds =
          key === 'contactIds'
            ? Array.isArray(selectedContact?.id)
              ? selectedContact.id
                  .map((val) => (val !== null && val !== undefined ? +val : null))
                  .filter((val) => Number.isFinite(val))
              : String(selectedContact?.id || '')
                  .split(',')
                  .map((val) => (val ? +val : null))
                  .filter((val) => Number.isFinite(val))
            : undefined;

        payload = {
          templateId: templateId,
          returnContactId:
            selectedReturnedAddress?.id === '-1' ? undefined : selectedReturnedAddress?.id,
          [key]:
            key === 'reqId'
              ? selectedContact
              : key === 'contactIds'
                ? contactIds || []
                : selectedContact?.id,
          productId:
            products.find(
              (product) =>
                product.productType === template?.object?.product?.productType &&
                product.deliveryType === (selectedPostage?.value || template?.object?.product?.deliveryType ||  template?.object?.product?.postageType) &&
                product.paperSize === template?.object?.product?.paperSize &&
                (selectedEnvelope ? product.envelopeType === selectedEnvelope.label : true)
            )?.id || template?.object?.product?.id
        };
      } else {
        const selectedCampaignTemplate = selectedCampaign?.templates?.[currentProofIndex];
        if (!selectedCampaignTemplate?.id) {
          dispatch(failure('Template ID is required'));
          return;
        }
        const productId =
          products.find(
            (product) =>
              product.productType === selectedCampaignTemplate?.productType &&
              product.deliveryType === selectedCampaignTemplate?.selectedPostage?.value &&
              product.paperSize === selectedCampaignTemplate?.postCardSize &&
              (selectedCampaignTemplate?.selectedEnvelope
                ? product.envelopeType === selectedCampaignTemplate?.selectedEnvelope?.label
                : true)
          )?.id || selectedCampaignTemplate?.productId;

        // Build contactIds as an array of numbers when multiple contacts are selected
        const contactIds =
          key === 'contactIds'
            ? Array.isArray(selectedContact?.id)
              ? selectedContact.id
                  .map((val) => (val !== null && val !== undefined ? +val : null))
                  .filter((val) => Number.isFinite(val))
              : String(selectedContact?.id || '')
                  .split(',')
                  .map((val) => (val ? +val : null))
                  .filter((val) => Number.isFinite(val))
            : undefined;

        payload = {
          templateId: +selectedCampaignTemplate.id,
          returnContactId:
            selectedReturnedAddress?.id === '-1' ? undefined : selectedReturnedAddress?.id,
          [key]:
            key === 'reqId'
              ? selectedContact
              : key === 'contactIds'
                ? contactIds || []
                : selectedContact?.id,
          productId,
        };
      }
      setDownloadingProof(true);
      if (selectionType?.id === 1) setDownloadingEnvelopeProof(true);
      await dispatch(createEnvelopeProof(label, payload));
      if (selectionType?.id === 1) {
        setEnvelopeProofDownloaded(true);
      }

      // Mark envelope proof as downloaded for the current template
      if (selectionType?.id === 2 && selectedCampaign) {
        const template = selectedCampaign.templates?.[currentProofIndex];
        if (template) {
          template.isEnvelopeDownloaded = true;
          // Trigger validation recalculation
          setDownloadUpdateTrigger((prev) => prev + 1);
        }

        // Auto-check only when ALL required proofs (mailer + envelope, if any) are downloaded
        if (areAllRequiredProofsDownloaded() && !checkedIds.includes(1)) {
          setCheckedIds((prev) => [...prev, 1]);
        }
      } else if (selectionType?.id === 1 && selectedTemplate) {
        selectedTemplate.isEnvelopeDownloaded = true;
        // Trigger validation recalculation
        setDownloadUpdateTrigger((prev) => prev + 1);
        // Auto-check only when ALL required proofs are downloaded
        if (areAllRequiredProofsDownloaded() && !checkedIds.includes(1)) {
          setCheckedIds((prev) => [...prev, 1]);
        }
      }
      return true;
    } catch (error) {
      console.log('Error downloading envelope proof:', error);
      dispatch(failure(MESSAGES.VIEW_PROOF_ERROR));
      return false;
    } finally {
      setDownloadingProof(false);
      if (selectionType?.id === 1) setDownloadingEnvelopeProof(false);
      setDownloadingRows((prev) =>
        prev.filter((row) => !(row.index === currentProofIndex && row.type === 'envelope'))
      );
    }
  };

  useEffect(() => {
    if (forntImage) {
      setImageLoading(true);
    } else if (!forntImage) {
      setTabValue({
        type: 'mail_proof',
      });
    }
  }, [forntImage]);

  // Sync template preview thumbnails from selected template (align with MailerDetails)
  useEffect(() => {
    if (selectedTemplate?.frontThumbnailUrl || selectedTemplate?.backThumbnailUrl) {
      setImageLoading(true);
      setTemplatePreview({
        templateUrl: selectedTemplate?.frontThumbnailUrl || null,
        backTemplateUrl: selectedTemplate?.backThumbnailUrl || null,
      });
      setIsBackSide(false);
    } else {
      setTemplatePreview({ templateUrl: null, backTemplateUrl: null });
    }
  }, [selectedTemplate]);

  useEffect(() => {
    if (activePreviewImageUrl) {
      setImageLoading(true);
    }
  }, [activePreviewImageUrl]);

  // Clear error when all required checkboxes are checked
  useEffect(() => {
    if (areAllRequiredCheckboxesChecked && errors) {
      updateErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  }, [areAllRequiredCheckboxesChecked, errors, name, updateErrors]);

  // Notify parent when validation status changes
  // This effect runs whenever validation-related state changes
  useEffect(() => {
    if (onValidationChange) {
      // Use a small delay to ensure all state updates have been processed
      const timer = setTimeout(() => {
        onValidationChange(isReviewValid);
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isReviewValid, onValidationChange, checkedIds, downloadUpdateTrigger]);

  return (
    <>
      <Box
        className={`reviewOrderContainer ${
          selectionType?.id === 2 ? 'reviewOrderContainer--campaign' : ''
        }`.trim()}
      >
        <Box className={`leftColumn ${activeItemId === 4 ? 'reviewLeftColumn' : ''}`}>
          <Box className="reviewOrderCheckWrapper">
            <Typography>{MESSAGES.ORDERS.CREATE.REVIEW.HEADING}</Typography>
            <FormGroup style={{ marginTop: '24px' }}>
              {checkList.map((content, id) => {
                const isProofRelated = content.id === 1 || content.id === 2;
                const showProgress = isProofRelated && selectionType?.id === 2;

                return (
                  <FormControlLabel
                    key={id}
                    className={`reviewOrderChecks ${checkedIds.includes(content.id) ? 'checked' : ''} ${showProgress ? 'withProgress' : ''}`}
                    control={
                      <Checkbox
                        // className={`customCheckbox ${
                        //   checkedIds.includes(content.id) ? 'checked' : ''
                        // }`}
                        checked={checkedIds.includes(content.id)}
                        onClick={() => handleCheckboxChange(content.id)}
                        icon={<img src={UncheckedCIcon} alt="unchecked" />}
                        checkedIcon={<img src={CheckedCIcon} alt="checked" />}
                      />
                    }
                    label={
                      <Box className={showProgress ? 'proofLabelWithProgress' : ''}>
                        <Typography className="checkboxLabel">{content.label}</Typography>
                      </Box>
                    }
                  />
                );
              })}
              {errors && (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {MESSAGES.ORDERS.CREATE.REVIEW.REQUIRED}
                </Typography>
              )}
            </FormGroup>
            {/* <Typography className="cancelOrderDate">
              This order can be canceled up until 11:59 PM on Sept 27, 2025
            </Typography> */}
          </Box>
        </Box>
        <Box
          className={`rightColumn ${activeItemId === 4 ? 'reviewRightColumn' : ''}`}
          sx={{
            display: 'flex',
            justifyContent: isTemplateAvailable ? 'end' : 'flex-start',
            // width: '100%',
          }}
        >
          {selectionType?.id === 1 && selectedTemplate?.id && (
            <Box
              className="previewPartEnd"
              style={{
                display: !hasEnvelope ? 'flex' : '',
                alignItems: !hasEnvelope ? 'center' : 'flex-start',
              }}
            >
              <Box className="previewTabs">
                <Box className="previewTabItem">
                  <Box
                    role="button"
                    tabIndex={0}
                    className={`previewTab ${tabValue?.type === 'mail_proof' ? 'active' : ''}`}
                    onClick={(e) => handleTabChange(e, 'mail_proof')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleTabChange(e, 'mail_proof');
                    }}
                  >
                    <Typography>Mailer Proof</Typography>
                  </Box>
                  <Box
                    component="button"
                    type="button"
                    className="proofDownloadIconButton"
                    aria-label={MESSAGES.ORDERS.CREATE.MAILER_PROOF_LABEL}
                    title={MESSAGES.ORDERS.CREATE.MAILER_PROOF_LABEL}
                    onClick={() => downloadViewProof(null)}
                    disabled={downloadingMailerProof}
                  >
                    {downloadingMailerProof ? (
                      <CircularProgress size={14} sx={{ color: '#ed5c2f' }} />
                    ) : (
                      <img src={MailerProofReviewCIcon} alt="" />
                    )}
                  </Box>
                </Box>
                {hasEnvelope && (
                  <Box className="previewTabItem">
                    <Box
                      role="button"
                      tabIndex={0}
                      className={`previewTab ${
                        tabValue?.type === 'envelope_proof' ? 'active' : ''
                      }`}
                      onClick={(e) => handleTabChange(e, 'envelope_proof')}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleTabChange(e, 'envelope_proof');
                      }}
                    >
                      <Typography>Envelope Proof</Typography>
                    </Box>
                    <Box
                      component="button"
                      type="button"
                      className="proofDownloadIconButton"
                      aria-label={MESSAGES.ORDERS.CREATE.ENVELOPE_PROOF_LABEL}
                      title={MESSAGES.ORDERS.CREATE.ENVELOPE_PROOF_LABEL}
                      onClick={() => downloadEnvelopeProof(null)}
                      disabled={downloadingEnvelopeProof}
                    >
                      {downloadingEnvelopeProof ? (
                        <CircularProgress size={14} sx={{ color: '#ed5c2f' }} />
                      ) : (
                        <img src={MailerProofReviewCIcon} alt="" />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
              <Box
                className={`previewPartContent ${!hasEnvelope ? 'reviewPreviewPartContent' : ''}`}
                sx={{ position: 'relative', minHeight: '300px' }}
              >
                {previewTab === 'mailer' ? (
                  <>
                    {!mailerImageUrl ? (
                      <Box
                        sx={{
                          height: '300px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <CircularProgress
                          sx={{
                            color: '#ed5c2f',
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <img
                          key={`${isBackSide && templatePreview.backTemplateUrl ? templatePreview.backTemplateUrl : mailerImageUrl}`}
                          src={
                            isBackSide && templatePreview.backTemplateUrl
                              ? templatePreview.backTemplateUrl
                              : mailerImageUrl
                          }
                          alt="preview"
                          onLoad={() => setImageLoading(false)}
                          onError={() => setImageLoading(false)}
                          style={{
                            display: imageLoading ? 'none' : 'block',
                          }}
                        />
                        {imageLoading && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <CircularProgress
                              sx={{
                                color: '#ed5c2f',
                              }}
                            />
                          </Box>
                        )}
                        {templatePreview.backTemplateUrl ? (
                          <>
                            <Box
                              role="button"
                              tabIndex={0}
                              className="leftArrowIcon"
                              onClick={() => {
                                setImageLoading(true);
                                setIsBackSide((p) => !p);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setImageLoading(true);
                                  setIsBackSide((p) => !p);
                                }
                              }}
                            />
                            <Box
                              role="button"
                              tabIndex={0}
                              className="rightArrowIcon"
                              onClick={() => {
                                setImageLoading(true);
                                setIsBackSide((p) => !p);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  setImageLoading(true);
                                  setIsBackSide((p) => !p);
                                }
                              }}
                            />
                          </>
                        ) : null}
                      </>
                    )}
                  </>
                ) : previewTab === 'envelope' ? (
                  <>
                    {!envelopeImage ? (
                      <Box
                        sx={{
                          height: '300px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <CircularProgress
                          sx={{
                            color: '#ed5c2f',
                          }}
                        />
                      </Box>
                    ) : (
                      <>
                        <img
                          key={`${envelopeImage}-${selectedEnvelope?.label || 'default'}`}
                          src={envelopeImage}
                          alt="envelope preview"
                          onLoad={() => {
                            setImageLoading(false);
                          }}
                          onError={() => {
                            setImageLoading(false);
                          }}
                          style={{
                            display: imageLoading ? 'none' : 'block',
                          }}
                        />
                        {imageLoading && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <CircularProgress
                              sx={{
                                color: '#ed5c2f',
                              }}
                            />
                          </Box>
                        )}
                      </>
                    )}
                  </>
                ) : null}
              </Box>
            </Box>
          )}
          {selectionType?.id === 2 && selectedCampaign && (
            <OrderedProducts
              selectedCampaign={selectedCampaign}
              downloadViewProof={downloadViewProof}
              downloadEnvelopeProof={downloadEnvelopeProof}
              downloadingRows={downloadingRows}
              activeItemId={activeItemId}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default ReviewOrder;
